/**
 * Probate Complexity Scorer
 *
 * Estimates how complex the probate administration is likely to be.
 * Helps executors understand whether the estate is simple or likely to
 * require professional help. Helps solicitors and advisers triage.
 *
 * This is NOT a tax calculation — it is a case-complexity indicator
 * that sits on top of existing estate data, tax outputs, and risk logic.
 */

import { IHTCalculation, EstateAssets, EstateProfile } from '../../types';

// ── Types ──────────────────────────────────────────────────────────────

export type ComplexityLevel = 'Low' | 'Medium' | 'High' | 'Very High';

export type ComplexityFactor = {
    id: string;
    label: string;
    points: number;
    detail?: string;
    category: 'estate_size' | 'property' | 'gifts' | 'tax_filing' | 'relief' | 'ownership' | 'administration';
};

export type ComplexityAssessment = {
    score: number;
    level: ComplexityLevel;
    summary: string;
    factors: ComplexityFactor[];
};

// ── Configurable thresholds & weights ──────────────────────────────────
// All weights in one place for easy tuning after real-world usage.

export const COMPLEXITY_THRESHOLDS = {
    LOW_MAX: 2,
    MEDIUM_MAX: 5,
    HIGH_MAX: 8,
    // 9+ = Very High
} as const;

export const COMPLEXITY_WEIGHTS = {
    // A. Estate size
    estateExceedsNRB: 1,
    estateExceeds1M: 1,
    estateExceeds2M: 1,

    // B. Property
    multipleProperties: 1,
    tenantsInCommon: 1,
    jointPropertyAssumedShare: 1,
    propertyWithMortgage: 1,

    // C. Gifts
    giftsWithin7Years: 1,
    chargeableGiftsExceed50PctNRB: 1,
    giftTaxArises: 2,
    incompleteGiftDates: 1,

    // D. Tax / filing
    ihtPayable: 2,
    fullIHT400Route: 1,
    moreThan3Schedules: 1,
    rnrbClaimed: 1,
    rnrbTaper: 1,
    tnrbOrTrnrbClaimed: 1,

    // E. Relief
    aprBprClaimed: 2,
    normalExpenditureExemption: 1,

    // F. Ownership / probate
    probateLikelyRequired: 1,
    jointAssetsNeedVerification: 1,
    willMissing: 1,

    // G. Administration
    liquidityShortfall: 1,
    propertyDominatedEstate: 1,
    debtsPresent: 1,

    // H. Special factors
    trustInvolved: 2,
    foreignAssets: 1,
    splitInheritance: 1,
} as const;

// ── Level thresholds ───────────────────────────────────────────────────

function getLevel(score: number): ComplexityLevel {
    if (score <= COMPLEXITY_THRESHOLDS.LOW_MAX) return 'Low';
    if (score <= COMPLEXITY_THRESHOLDS.MEDIUM_MAX) return 'Medium';
    if (score <= COMPLEXITY_THRESHOLDS.HIGH_MAX) return 'High';
    return 'Very High';
}

const LEVEL_SUMMARIES: Record<ComplexityLevel, string> = {
    Low: 'This appears to be a relatively straightforward estate from an administration perspective.',
    Medium: 'This estate has some complicating features and may require more detailed probate and tax work.',
    High: 'Multiple complicating factors are present. Professional advice from a probate solicitor or accountant is strongly recommended.',
    'Very High': 'This is a highly complex estate with several tax, ownership, or administration issues. Full professional handling is strongly recommended.',
};

// ── Factor detection ───────────────────────────────────────────────────

export function assessComplexity(
    ihtCalc?: IHTCalculation | null,
    assets?: EstateAssets,
    profile?: EstateProfile,
): ComplexityAssessment {
    const factors: ComplexityFactor[] = [];
    const W = COMPLEXITY_WEIGHTS;
    const bd = ihtCalc?.breakdown;

    // ── A. Estate size ─────────────────────────────────────────────
    const net = bd?.net_value ?? 0;
    if (net > 2_000_000) {
        factors.push({ id: 'estate_2m', label: 'Estate exceeds £2M', points: W.estateExceeds2M + W.estateExceeds1M + W.estateExceedsNRB, detail: `Net estate: £${net.toLocaleString()}`, category: 'estate_size' });
    } else if (net > 1_000_000) {
        factors.push({ id: 'estate_1m', label: 'Estate exceeds £1M', points: W.estateExceeds1M + W.estateExceedsNRB, detail: `Net estate: £${net.toLocaleString()}`, category: 'estate_size' });
    } else if (net > 325_000) {
        factors.push({ id: 'estate_nrb', label: 'Estate exceeds NRB (£325k)', points: W.estateExceedsNRB, detail: `Net estate: £${net.toLocaleString()}`, category: 'estate_size' });
    }

    // ── B. Property complexity ─────────────────────────────────────
    const props = assets?.properties ?? [];
    if (props.length > 1) {
        factors.push({ id: 'multi_prop', label: 'Multiple properties', points: W.multipleProperties, detail: `${props.length} properties in the estate`, category: 'property' });
    }

    if (props.some(p => p.ownership_type === 'tenants_in_common')) {
        factors.push({ id: 'tic', label: 'Tenants in common ownership', points: W.tenantsInCommon, detail: 'Requires valuation of deceased\'s share', category: 'property' });
    }

    const jointAssumedShare = props.some(p =>
        (p.ownership_type === 'joint_tenants' || p.ownership_type === 'tenants_in_common') &&
        (p.deceased_share_percent === undefined || p.deceased_share_percent === null)
    );
    if (jointAssumedShare) {
        factors.push({ id: 'assumed_share', label: 'Joint property with assumed beneficial share', points: W.jointPropertyAssumedShare, detail: 'Ownership share not confirmed — may need verification', category: 'property' });
    }

    if (props.some(p => p.has_mortgage)) {
        factors.push({ id: 'mortgage', label: 'Mortgage on property', points: W.propertyWithMortgage, detail: 'Mortgage balance must be confirmed and deducted', category: 'property' });
    }

    // ── C. Gifts ───────────────────────────────────────────────────
    const gifts = assets?.lifetime_gifts ?? [];
    const giftsExist = gifts.length > 0 || assets?.gifts_last_7_years?.exists === true;
    const chargeableGifts = bd?.gifts_chargeable_total ?? 0;
    const giftTax = bd?.gift_tax_after_taper ?? bd?.gift_tax_before_taper ?? 0;

    if (giftTax > 0) {
        factors.push({ id: 'gift_tax', label: 'Gift tax arises', points: W.giftTaxArises, detail: `Tax on gifts: £${giftTax.toLocaleString()}`, category: 'gifts' });
    } else if (chargeableGifts > 162_500) { // 50% of NRB
        factors.push({ id: 'gifts_50pct_nrb', label: 'Chargeable gifts exceed 50% of NRB', points: W.chargeableGiftsExceed50PctNRB, detail: `£${chargeableGifts.toLocaleString()} in chargeable gifts`, category: 'gifts' });
    } else if (giftsExist) {
        factors.push({ id: 'gifts_7y', label: 'Lifetime gifts within 7 years', points: W.giftsWithin7Years, category: 'gifts' });
    }

    const incompleteGifts = gifts.filter(g => !g.date || !g.amount || g.amount <= 0);
    if (incompleteGifts.length > 0) {
        factors.push({ id: 'gift_dates', label: 'Incomplete gift records', points: W.incompleteGiftDates, detail: `${incompleteGifts.length} gift${incompleteGifts.length !== 1 ? 's' : ''} missing dates or amounts`, category: 'gifts' });
    }

    // ── D. Tax / filing complexity ─────────────────────────────────
    const totalTax = ihtCalc?.total_tax_due ?? 0;
    if (totalTax > 0) {
        factors.push({ id: 'iht_payable', label: 'IHT is payable', points: W.ihtPayable, detail: `£${totalTax.toLocaleString()} tax due`, category: 'tax_filing' });
    }

    // Estimate whether IHT400 route — using same logic indicators
    const isLikelyIHT400 = totalTax > 0 || (bd?.gross_value ?? 0) > 3_000_000 ||
        chargeableGifts > 0 || (bd?.bpr_relief_total ?? 0) > 0 || (bd?.apr_relief_total ?? 0) > 0;
    if (isLikelyIHT400) {
        factors.push({ id: 'iht400', label: 'Full IHT400 route required', points: W.fullIHT400Route, category: 'tax_filing' });
    }

    // Count likely supplementary schedules
    let scheduleCount = 0;
    if (props.length > 0) scheduleCount++; // IHT405
    if ((assets?.bank_accounts ?? []).length > 0) scheduleCount++; // IHT406
    if ((assets?.investments ?? []).length > 0) scheduleCount++; // IHT411
    if (giftsExist) scheduleCount++; // IHT403
    const hasJoint = props.some(p => p.ownership_type === 'joint_tenants' || p.ownership_type === 'tenants_in_common') ||
        (assets?.bank_accounts ?? []).some(b => b.ownership === 'joint') ||
        (assets?.investments ?? []).some(i => i.ownership === 'joint');
    if (hasJoint) scheduleCount++; // IHT404
    if (isLikelyIHT400 && scheduleCount > 3) {
        factors.push({ id: 'many_schedules', label: 'Multiple HMRC schedules required', points: W.moreThan3Schedules, detail: `${scheduleCount} supplementary schedules likely`, category: 'tax_filing' });
    }

    if ((bd?.rnrb_available ?? 0) > 0 || (bd?.rnrb_before_taper ?? 0) > 0) {
        factors.push({ id: 'rnrb', label: 'RNRB claimed', points: W.rnrbClaimed, detail: `£${(bd?.rnrb_available ?? 0).toLocaleString()} Residence Nil Rate Band`, category: 'tax_filing' });
    }

    if ((bd?.taper_amount ?? 0) > 0) {
        factors.push({ id: 'rnrb_taper', label: 'RNRB taper applies', points: W.rnrbTaper, detail: `£${(bd!.taper_amount!).toLocaleString()} tapered away`, category: 'tax_filing' });
    }

    const sp = profile?.iht_details?.predeceased_spouse;
    if (sp?.exists === true && ((sp.unused_nrb_percent ?? 0) > 0 || (sp.unused_rnrb_percent ?? 0) > 0)) {
        factors.push({ id: 'tnrb', label: 'Transferable NRB/RNRB claimed', points: W.tnrbOrTrnrbClaimed, detail: 'Transfer from predeceased spouse', category: 'tax_filing' });
    }

    // ── E. Relief complexity ───────────────────────────────────────
    const bpr = bd?.bpr_relief_total ?? 0;
    const apr = bd?.apr_relief_total ?? 0;
    if (bpr > 0 || apr > 0) {
        const parts: string[] = [];
        if (bpr > 0) parts.push(`BPR £${bpr.toLocaleString()}`);
        if (apr > 0) parts.push(`APR £${apr.toLocaleString()}`);
        factors.push({ id: 'apr_bpr', label: 'APR/BPR relief claimed', points: W.aprBprClaimed, detail: parts.join(', '), category: 'relief' });
    }

    const hasIncomeExemption = gifts.some(g => g.is_regular_from_income);
    if (hasIncomeExemption) {
        factors.push({ id: 'income_exemption', label: 'Normal expenditure exemption claimed', points: W.normalExpenditureExemption, detail: 'Requires income/expenditure evidence (IHTA 1984 s21)', category: 'relief' });
    }

    // ── F. Ownership / probate ─────────────────────────────────────
    const probateRequired = props.some(p => p.ownership_type === 'sole' || p.ownership_type === 'tenants_in_common') ||
        (assets?.bank_accounts ?? []).some(b => b.ownership === 'sole') ||
        (assets?.investments ?? []).some(i => i.ownership === 'sole');
    if (probateRequired) {
        factors.push({ id: 'probate', label: 'Probate likely required', points: W.probateLikelyRequired, category: 'ownership' });
    }

    const unknownOwnership = [
        ...props.filter(p => !p.ownership_type || p.ownership_type === 'unknown'),
        ...(assets?.bank_accounts ?? []).filter(a => !a.ownership),
        ...(assets?.investments ?? []).filter(i => !i.ownership),
    ];
    if (hasJoint || unknownOwnership.length > 0) {
        if (unknownOwnership.length > 0) {
            factors.push({ id: 'ownership_verify', label: 'Ownership verification needed', points: W.jointAssetsNeedVerification, detail: `${unknownOwnership.length} asset${unknownOwnership.length !== 1 ? 's' : ''} with unconfirmed ownership`, category: 'ownership' });
        }
    }

    if (profile?.representatives?.will_exists !== 'yes' && profile?.representatives?.will_exists !== 'no') {
        factors.push({ id: 'will_missing', label: 'Will status not confirmed', points: W.willMissing, category: 'ownership' });
    }

    // ── G. Administration difficulty ───────────────────────────────
    if (totalTax > 0) {
        const liquid = (assets?.bank_accounts ?? []).reduce((s, a) => {
            const val = Number(a.estimated_value) || 0;
            return s + (a.ownership === 'joint' ? val / 2 : val);
        }, 0);
        if (liquid < totalTax) {
            factors.push({ id: 'liquidity', label: 'Liquid assets below tax due', points: W.liquidityShortfall, detail: `Cash: £${liquid.toLocaleString()} vs tax: £${totalTax.toLocaleString()}`, category: 'administration' });
        }
    }

    const gross = bd?.gross_value ?? 0;
    if (gross > 0) {
        const propVal = props.reduce((s, p) => {
            // Net equity × share, matching gross_value (avoids >100% ratios).
            const val = Number(p.estimated_value) || 0;
            const mortgage = p.has_mortgage ? (Number(p.mortgage_balance) || 0) : 0;
            const netEquity = Math.max(0, val - mortgage);
            const share = (p.deceased_share_percent ?? 100) / 100;
            return s + netEquity * share;
        }, 0);
        if (propVal / gross > 0.6) {
            factors.push({ id: 'prop_heavy', label: 'Property-dominated estate', points: W.propertyDominatedEstate, detail: `Property is ${Math.round(propVal / gross * 100)}% of gross estate`, category: 'administration' });
        }
    }

    if ((assets?.debts ?? []).length > 0) {
        factors.push({ id: 'debts', label: 'Debts or liabilities present', points: W.debtsPresent, detail: `${assets!.debts.length} debt${assets!.debts.length !== 1 ? 's' : ''} recorded`, category: 'administration' });
    }

    // ── H. Special factors ─────────────────────────────────────────
    const trustScreening = profile?.iht_details?.trust_screening;
    const hasTrust = trustScreening?.has_trusts === true || assets?.trusts_or_life_interest?.exists === true;
    if (hasTrust) {
        factors.push({ id: 'trust', label: 'Trust involved', points: W.trustInvolved, detail: 'Trusts require specialist IHT treatment', category: 'administration' });
    }

    if (assets?.foreign_assets?.exists === true) {
        factors.push({ id: 'foreign', label: 'Foreign assets', points: W.foreignAssets, detail: 'May require foreign probate or succession procedures', category: 'administration' });
    }

    const hasSplitInheritance = props.some(p =>
        p.beneficiary_allocations && p.beneficiary_allocations.length > 1
    );
    if (hasSplitInheritance) {
        factors.push({ id: 'split_inheritance', label: 'Split inheritance across beneficiaries', points: W.splitInheritance, category: 'administration' });
    }

    // ── Assemble ───────────────────────────────────────────────────
    const score = factors.reduce((sum, f) => sum + f.points, 0);
    const level = getLevel(score);

    return {
        score,
        level,
        summary: LEVEL_SUMMARIES[level],
        factors,
    };
}
